import type { Game, GameProgress, ModuleResult, Reward } from '../types/game';

const keyFor = (gameId: number) => `interactive-game:${gameId}:progress`;
const unique = (values: number[]) => [...new Set(values)];

function isProgress(value: unknown, gameId: number): value is GameProgress {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<GameProgress>;
  return candidate.version === 1 && candidate.gameId === gameId &&
    typeof candidate.currentChapterId === 'number' && typeof candidate.currentModuleId === 'number' &&
    Array.isArray(candidate.completedModules) && Array.isArray(candidate.collectedItems);
}

export const ProgressService = {
  create(game: Game): GameProgress {
    const firstChapter = game.chapters[0];
    const firstModule = firstChapter?.modules[0];
    if (!firstChapter || !firstModule) throw new Error('El juego no tiene módulos configurados.');
    const now = new Date().toISOString();
    return {
      version: 1, gameId: game.id, currentChapterId: firstChapter.id, currentModuleId: firstModule.id,
      completedModules: [], collectedItems: [], attempts: {}, startedAt: now, lastPlayedAt: now,
    };
  },
  load(game: Game): GameProgress | null {
    try {
      const raw = localStorage.getItem(keyFor(game.id));
      if (!raw) return null;
      const value: unknown = JSON.parse(raw);
      if (!isProgress(value, game.id)) return null;
      const moduleExists = game.chapters.some((chapter) => chapter.id === value.currentChapterId &&
        chapter.modules.some((module) => module.id === value.currentModuleId));
      return moduleExists ? value : null;
    } catch { return null; }
  },
  save(progress: GameProgress): void {
    localStorage.setItem(keyFor(progress.gameId), JSON.stringify({ ...progress, lastPlayedAt: new Date().toISOString() }));
  },
  complete(progress: GameProgress, moduleId: number, result: ModuleResult, reward: Reward | null): GameProgress {
    const collected = reward && !progress.collectedItems.some((item) => item.id === reward.id)
      ? [...progress.collectedItems, { ...reward, collectedAt: new Date().toISOString() }]
      : progress.collectedItems;
    return {
      ...progress,
      completedModules: unique([...progress.completedModules, moduleId]),
      collectedItems: collected,
      attempts: result.attempts === undefined ? progress.attempts : { ...progress.attempts, [moduleId]: result.attempts },
      lastPlayedAt: new Date().toISOString(),
    };
  },
  reset(gameId: number): void { localStorage.removeItem(keyFor(gameId)); },
};
